import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import auth from "../../fire";
import { applyActionCode } from "firebase/auth";
import { ClipLoader } from "react-spinners";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import AuthActionHeader from "./AuthActionHeader";

type VerifyStatus = "verifying" | "success" | "error";

interface VerifyEmailActionProps {
  oobCode: string | null;
}

// Applies a Firebase `verifyEmail` oobCode. oobCodes are single-use, so the
// run-once ref guards against React StrictMode's double effect invocation in dev
// (a second applyActionCode would fail and flip a success back to error).
const VerifyEmailAction = ({ oobCode }: VerifyEmailActionProps) => {
  const [status, setStatus] = useState<VerifyStatus>("verifying");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const run = async () => {
      if (!oobCode) {
        setStatus("error");
        return;
      }
      try {
        await applyActionCode(auth, oobCode);
        // If the user is logged in, refresh so emailVerified updates locally.
        if (auth.currentUser) {
          try {
            await auth.currentUser.reload();
          } catch (e) {
            /* non-fatal */
          }
        }
        setStatus("success");
      } catch (e) {
        setStatus("error");
      }
    };
    run();
  }, [oobCode]);

  if (status === "verifying") {
    return (
      <div className="flex items-center justify-center py-24">
        <ClipLoader
          color="rgb(79 70 229)"
          speedMultiplier={0.6}
          size={"2rem"}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col py-12 px-4">
      <AuthActionHeader />
      {status === "success" ? (
        <>
          <CheckCircleIcon
            className="h-auto w-12 mx-auto text-indigo-600"
            aria-hidden="true"
          />
          <h2 className="text-center mt-6 text-3xl font-bold tracking-tight text-gray-900">
            Email verified
          </h2>
          <p className="text-center text-sm text-gray-900 mt-2">
            Thanks! Your email address has been verified.
          </p>
        </>
      ) : (
        <>
          <ExclamationTriangleIcon
            className="h-auto w-12 mx-auto text-red-500"
            aria-hidden="true"
          />
          <h2 className="text-center mt-6 text-2xl font-bold tracking-tight text-gray-900">
            Verification link issue
          </h2>
          <p className="text-center text-sm text-gray-900 mt-2">
            This verification link is invalid or has expired. You can request a
            new one from your account page.
          </p>
        </>
      )}
      <div className="px-4 py-4 w-full flex justify-center fixed bottom-0 bg-gray-50">
        <Link to="/login" className="text-sm text-indigo-600 font-medium">
          Back to login
        </Link>
      </div>
    </div>
  );
};

export default VerifyEmailAction;
